/**
 * Auto-placement grid for seating tables — keeps centres inside the furniture
 * layer with margin for plate, seats and labels.
 */

import type { SeatingCanvasInset, TableShape } from "@amazing-wedding/shared-types";
import {
  clampSeatingCanvasInset,
  DEFAULT_SEATING_CANVAS_INSET,
} from "@amazing-wedding/shared-types";
import {
  buildSeatSlots,
  labelOffsetsForShape,
  MAX_LABEL_WIDTH,
  PIP_RADIUS,
  plateSize,
  LABEL_FONT_SIZE,
  LABEL_LINE_HEIGHT,
} from "./tableGeometry";
import { REFERENCE_CANVAS, VIP_TABLE_CAPACITY } from "./constants";

/** Default plan room width (m) — matches plan-operations seed. */
export const DEFAULT_SEATING_ROOM_WIDTH_M = 20;

/** Minimum walkway between table footprints (plate + seats + labels) in metres. */
export const MIN_TABLE_AISLE_M = 1.0;

const MARGIN_PCT_MIN = 10;
const MARGIN_PCT_MAX = 18;

export interface TableLayoutOptions {
  maxCapacity?: number;
  shape?: TableShape;
  /** Reserve margin for the VIP head table footprint when it is index 0. */
  includeVipTable?: boolean;
  marginPct?: number;
}

export interface TablePosition {
  positionX: number;
  positionY: number;
}

export interface DefaultTablePositionOptions extends TableLayoutOptions {
  totalTables?: number;
}

export interface SpaciousTableLayoutOptions extends TableLayoutOptions {
  canvasInset?: SeatingCanvasInset;
  roomWidthM?: number;
}

export interface SpaciousTableLayoutResult {
  positions: TablePosition[];
  /** Non-null when the default room was too small and inset/width were expanded. */
  planLayout: { seatingCanvasInset: SeatingCanvasInset; seatingRoomWidthM: number } | null;
}

/** Extra px added to required furniture span so clamp/rounding never shrink below aisle gap. */
const REQUIRED_SPAN_BUFFER_PX = 8;

function mergeCanvasInset(inset?: SeatingCanvasInset): SeatingCanvasInset {
  return inset ? { ...DEFAULT_SEATING_CANVAS_INSET, ...inset } : DEFAULT_SEATING_CANVAS_INSET;
}

function pixelsPerMeterFor(inset: SeatingCanvasInset, roomWidthM: number): number {
  const { w } = furniturePx(inset);
  return w / roomWidthM;
}

function gridDimensions(count: number): { cols: number; rows: number } {
  const aspect = REFERENCE_CANVAS.w / REFERENCE_CANVAS.h;
  const cols = Math.min(count, Math.max(1, Math.ceil(Math.sqrt(count * aspect))));
  const rows = Math.ceil(count / cols);
  return { cols, rows };
}

function maxTableHalfExtentPx(options: TableLayoutOptions, ppm: number): number {
  const capacity = Math.min(30, Math.max(2, Math.round(options.maxCapacity ?? 8)));
  const shape = options.shape ?? "round";
  let halfPx = estimateTableHalfExtentPx(capacity, shape, ppm);
  if (options.includeVipTable) {
    const vipHalf = estimateTableHalfExtentPx(VIP_TABLE_CAPACITY, "rectangular", ppm);
    halfPx = Math.max(halfPx, vipHalf);
  }
  return halfPx;
}

function requiredFurnitureSpanPx(cells: number, minStepPx: number, edgePaddingPx: number): number {
  if (cells <= 1) return 2 * edgePaddingPx;
  return (cells - 1) * minStepPx + 2 * edgePaddingPx;
}

/** Symmetric inset per side so furniture span equals `requiredPx` on one axis. */
function symmetricInsetForSpan(requiredPx: number, referencePx: number): number {
  const furniturePct = (requiredPx / referencePx) * 100;
  return (100 - furniturePct) / 2;
}

function furnitureSpanPx(inset: SeatingCanvasInset): { w: number; h: number } {
  return furniturePx(inset);
}

/**
 * Derive symmetric inset + scaled room width so furniture meets `requiredW/H` in px
 * while keeping pixels-per-metre stable (table physical size unchanged).
 */
function planLayoutForRequiredFurniture(
  baseInset: SeatingCanvasInset,
  baseRoomWidthM: number,
  requiredW: number,
  requiredH: number,
): { seatingCanvasInset: SeatingCanvasInset; seatingRoomWidthM: number } {
  const base = furnitureSpanPx(baseInset);
  const inset: SeatingCanvasInset = {
    left: symmetricInsetForSpan(requiredW, REFERENCE_CANVAS.w),
    right: symmetricInsetForSpan(requiredW, REFERENCE_CANVAS.w),
    top: symmetricInsetForSpan(requiredH, REFERENCE_CANVAS.h),
    bottom: symmetricInsetForSpan(requiredH, REFERENCE_CANVAS.h),
  };
  const clamped = clampSeatingCanvasInset(inset);
  const scaled = furnitureSpanPx(clamped);
  const seatingRoomWidthM = baseRoomWidthM * (scaled.w / Math.max(1, base.w));
  return {
    seatingCanvasInset: clamped,
    seatingRoomWidthM: Math.round(seatingRoomWidthM * 10) / 10,
  };
}

function layoutCentresPx(
  count: number,
  cols: number,
  furnitureW: number,
  furnitureH: number,
  minStepPx: number,
): Array<{ x: number; y: number }> {
  const rows = Math.ceil(count / cols);
  const gridW = cols > 1 ? (cols - 1) * minStepPx : 0;
  const gridH = rows > 1 ? (rows - 1) * minStepPx : 0;
  const startX = (furnitureW - gridW) / 2;
  const startY = (furnitureH - gridH) / 2;

  const centres: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    centres.push({
      x: startX + col * minStepPx,
      y: startY + row * minStepPx,
    });
  }
  return centres;
}

function furniturePx(inset = DEFAULT_SEATING_CANVAS_INSET): { w: number; h: number } {
  const w = (REFERENCE_CANVAS.w * (100 - inset.left - inset.right)) / 100;
  const h = (REFERENCE_CANVAS.h * (100 - inset.top - inset.bottom)) / 100;
  return { w, h };
}

function defaultPixelsPerMeter(inset = DEFAULT_SEATING_CANVAS_INSET): number {
  const { w } = furniturePx(inset);
  return w / DEFAULT_SEATING_ROOM_WIDTH_M;
}

/** Half-size in px from table centre to farthest seat/label edge (conservative). */
export function estimateTableHalfExtentPx(
  capacity: number,
  shape: TableShape,
  pixelsPerMeter: number,
): number {
  const { width, height } = plateSize(capacity, shape, pixelsPerMeter);
  const slots = buildSeatSlots(capacity, width, height, shape, 0);
  const offsets = labelOffsetsForShape(shape);
  const labelPad =
    Math.max(offsets.side, offsets.end) +
    MAX_LABEL_WIDTH / 2 +
    LABEL_FONT_SIZE * LABEL_LINE_HEIGHT * 2;

  let maxR = Math.max(width, height) / 2;
  for (const slot of slots) {
    const dotR = Math.hypot(slot.dotX, slot.dotY) + PIP_RADIUS + labelPad;
    const labelR = Math.hypot(slot.labelX, slot.labelY) + labelPad;
    maxR = Math.max(maxR, dotR, labelR);
  }
  return maxR;
}

/** Margin (percent of furniture box) so table visuals stay inside 0–100 %. */
export function layoutMarginPct(options: TableLayoutOptions = {}): number {
  if (options.marginPct != null) {
    return Math.max(MARGIN_PCT_MIN, Math.min(MARGIN_PCT_MAX, options.marginPct));
  }

  const ppm = defaultPixelsPerMeter();
  const { w, h } = furniturePx();
  const minDim = Math.min(w, h);

  const capacity = Math.min(30, Math.max(2, Math.round(options.maxCapacity ?? 8)));
  const shape = options.shape ?? "round";

  let halfPx = estimateTableHalfExtentPx(capacity, shape, ppm);

  if (options.includeVipTable) {
    const vipHalf = estimateTableHalfExtentPx(VIP_TABLE_CAPACITY, "rectangular", ppm);
    halfPx = Math.max(halfPx, vipHalf);
  }

  const pct = (halfPx / minDim) * 100;
  return Math.max(MARGIN_PCT_MIN, Math.min(MARGIN_PCT_MAX, pct));
}

function roundPct(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Even grid of table centres inside the furniture layer.
 * Index 0 is top-left cell (VIP / first table).
 */
export function computeTablePositions(
  count: number,
  options: TableLayoutOptions = {},
): TablePosition[] {
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return [];

  const margin = layoutMarginPct(options);
  const aspect = REFERENCE_CANVAS.w / REFERENCE_CANVAS.h;
  const cols = Math.min(n, Math.max(1, Math.ceil(Math.sqrt(n * aspect))));
  const rows = Math.ceil(n / cols);
  const span = 100 - 2 * margin;
  const stepX = span / cols;
  const stepY = span / rows;

  const positions: TablePosition[] = [];
  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions.push({
      positionX: roundPct(margin + (col + 0.5) * stepX),
      positionY: roundPct(margin + (row + 0.5) * stepY),
    });
  }
  return positions;
}

/**
 * Bulk-setup grid with a minimum real-world aisle between table footprints.
 * Expands plan inset + room width when the default hall is too small.
 */
export function computeSpaciousTableLayout(
  count: number,
  options: SpaciousTableLayoutOptions = {},
): SpaciousTableLayoutResult {
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return { positions: [], planLayout: null };

  const baseInset = mergeCanvasInset(options.canvasInset);
  const baseRoomWidthM = options.roomWidthM ?? DEFAULT_SEATING_ROOM_WIDTH_M;
  const ppm = pixelsPerMeterFor(baseInset, baseRoomWidthM);
  const halfPx = maxTableHalfExtentPx(options, ppm);
  const minStepPx = 2 * halfPx + MIN_TABLE_AISLE_M * ppm;

  const { cols, rows } = gridDimensions(n);
  const requiredW = requiredFurnitureSpanPx(cols, minStepPx, halfPx);
  const requiredH = requiredFurnitureSpanPx(rows, minStepPx, halfPx);

  const baseFurniture = furnitureSpanPx(baseInset);
  const needsExpansion = requiredW > baseFurniture.w || requiredH > baseFurniture.h;

  let planLayout: SpaciousTableLayoutResult["planLayout"] = null;
  let furnitureW = baseFurniture.w;
  let furnitureH = baseFurniture.h;

  if (needsExpansion) {
    planLayout = planLayoutForRequiredFurniture(
      baseInset,
      baseRoomWidthM,
      requiredW + REQUIRED_SPAN_BUFFER_PX,
      requiredH + REQUIRED_SPAN_BUFFER_PX,
    );
    const expanded = furnitureSpanPx(planLayout.seatingCanvasInset);
    furnitureW = expanded.w;
    furnitureH = expanded.h;

    // If clamp still leaves the box too tight (~30+ tables), shrink step to fit.
    const maxStepX = cols > 1 ? (furnitureW - 2 * halfPx) / (cols - 1) : minStepPx;
    const maxStepY = rows > 1 ? (furnitureH - 2 * halfPx) / (rows - 1) : minStepPx;
    const effectiveStep = Math.min(minStepPx, maxStepX, maxStepY);
    const centres = layoutCentresPx(n, cols, furnitureW, furnitureH, effectiveStep);
    const positions = centres.map(({ x, y }) => ({
      positionX: roundPct((x / furnitureW) * 100),
      positionY: roundPct((y / furnitureH) * 100),
    }));
    return { positions, planLayout };
  }

  const centres = layoutCentresPx(n, cols, furnitureW, furnitureH, minStepPx);
  const positions = centres.map(({ x, y }) => ({
    positionX: roundPct((x / furnitureW) * 100),
    positionY: roundPct((y / furnitureH) * 100),
  }));
  return { positions, planLayout };
}

/** Default canvas position for a new table (matches bulk setup grid). */
export function defaultTablePosition(
  index: number,
  options: DefaultTablePositionOptions = {},
): TablePosition {
  const total = options.totalTables ?? index + 1;
  const positions = computeTablePositions(total, options);
  const pos = positions[index];
  if (!pos) {
    const fallback = computeTablePositions(index + 1, options);
    return fallback[index] ?? { positionX: 50, positionY: 50 };
  }
  return pos;
}
